import { DateHelper } from '@bryntum/schedulerpro';
import { format, isValid, max, min, parse } from 'date-fns';
import { cloneDeep } from 'lodash';
import { Calendar } from 'sr-types/lib/search/v1/graphql';
import { DEFAULT_LANG, TIME_CONSTANTS } from '../../../constants/constants';
import { API_DATE_FORMAT, API_TIME_FORMAT, mergeDateAndTime } from '../../../utils/dateTime';
import { DEFAULT_SCHEDULE_TIME_VALUE } from '../constants';
import { ScheduleFormWidgetValue, ScheduleHierarchyFormWidgetValue } from '../types';
import {
    checkIsTimeBasedDurationUnit,
    clearCalculatedFields,
    hasRequiredScheduleInputFields,
    makeCalculationInputFromScheduleValue,
    makeSafeError,
    makeScheduleValueFromCalculationResult,
    normalizeCalendarValue,
    normalizeDurationValue,
    normalizeTimeFields
} from './common-schedule-utils';
import {
    EndDateRequiredError,
    InvalidChildDateTimeError,
    InvalidDateTimeRangeError,
    MixedDurationSpecifiedError
} from './errors';
import type { ScheduleCalculationInput } from './schedule-calculator';
import { calculateSchedule, calculateScheduleList } from './schedule-calculator';
import { calculateNextStandAloneScheduleFormWidgetValue } from './stand-alone-schedule-utils';

export async function calculateNextScheduleHierarchyFormWidgetValue(
    nextValue: ScheduleHierarchyFormWidgetValue,
    locale: string,
    calendarsData: Calendar[] | undefined
): Promise<{ calculatedNextValue: ScheduleHierarchyFormWidgetValue; error?: Error }> {
    let { parent, children, useChildSchedules, sequential } = cloneDeep(nextValue);

    if (!useChildSchedules && sequential) {
        sequential = false;
    }

    let calculatedParentValue: ScheduleFormWidgetValue;
    let calculatedChildrenValues: ScheduleFormWidgetValue[];
    let calculatedNextValue: ScheduleHierarchyFormWidgetValue;
    let error: Error | undefined = undefined;

    if (useChildSchedules) {
        try {
            // 0. Normalize parent and children values
            const inputScheduleValues = [parent.value, ...children.map((c) => c.value)];

            const normalizedScheduleValues = inputScheduleValues
                .map(normalizeTimeFields)
                .map(normalizeDurationValue)
                .map((v) => normalizeCalendarValue(v, calendarsData));

            const normalizedParentValue = normalizedScheduleValues[0];

            let normalizedChildrenValues = normalizedScheduleValues.slice(1);
            if (sequential) {
                normalizedChildrenValues = normalizeSequentialChildrenStartDates(normalizedChildrenValues);
            }

            // 1. Calculate children schedules first
            const normalizedChildrenValuesWithRequiredInputs: ScheduleFormWidgetValue[] = [];
            const childIndicesWithRequiredInput: number[] = [];

            let foundChildWithMissingInput = false;
            for (let index = 0; index < normalizedChildrenValues.length; index++) {
                const child = normalizedChildrenValues[index];
                const childHasRequiredInputs = hasRequiredScheduleInputFields(child);

                if (childHasRequiredInputs) {
                    if (sequential && foundChildWithMissingInput) {
                        // In sequential mode, once a child with missing inputs is found,
                        // all subsequent children are ignored
                        childIndicesWithRequiredInput.push(-1);
                    } else {
                        const length = normalizedChildrenValuesWithRequiredInputs.push(child);
                        childIndicesWithRequiredInput.push(length - 1);
                    }
                } else {
                    childIndicesWithRequiredInput.push(-1);
                    foundChildWithMissingInput = true;
                }
            }

            const childCalculationInputs = normalizedChildrenValuesWithRequiredInputs.map((v, i) =>
                makeCalculationInputFromScheduleValue(v, locale)
            );

            const childrenCalculationResultWithRequiredValues = await calculateScheduleList(
                childCalculationInputs,
                sequential
            );

            calculatedChildrenValues = normalizedChildrenValues.map((normalizedChildValue, index) => {
                const childIndexWithRequiredInput = childIndicesWithRequiredInput[index];
                if (childIndexWithRequiredInput >= 0) {
                    const childCalculationResultWithRequiredValues =
                        childrenCalculationResultWithRequiredValues[childIndexWithRequiredInput];
                    const calculatedChildValue = makeScheduleValueFromCalculationResult(
                        normalizedChildValue,
                        childCalculationResultWithRequiredValues
                    );
                    return calculatedChildValue;
                } else {
                    return clearCalculatedFields(normalizedChildValue);
                }
            });

            // 2. Calculate parent schedule based on date bounds of children
            const calculatedChildrenValuesWithRequiredInput = calculatedChildrenValues.filter((_, index) => {
                return childIndicesWithRequiredInput[index] >= 0;
            });

            calculatedParentValue = await calculateParentBoundsFromChildren(
                calculatedChildrenValuesWithRequiredInput,
                normalizedParentValue,
                locale
            );
        } catch (e) {
            calculatedNextValue = nextValue;

            error = makeSafeError(e, 'Unknown error during schedule hierarchy calculation');

            return { calculatedNextValue, error };
        }
    } else {
        const { calculatedNextValue, error: parentCalculationError } =
            await calculateNextStandAloneScheduleFormWidgetValue(parent.value, locale, calendarsData);

        calculatedParentValue = calculatedNextValue;
        error = parentCalculationError;

        calculatedChildrenValues = children.map((child) => ({
            ...(error ? child.value : calculatedNextValue),
            durationSpecified: child.value.durationSpecified,
            name: child.value.name,
            location: child.value.location
        }));
    }

    calculatedNextValue = {
        parent: {
            id: parent.id,
            value: calculatedParentValue
        },
        children: children.map((child, index) => ({ id: child.id, value: calculatedChildrenValues[index] })),
        useChildSchedules,
        sequential
    };

    return { calculatedNextValue, error };
}

/**
 * Normalizes start dates for sequential children by setting each child's start date
 * to the previous child's end date + 1 day.
 *
 * The first child's start date remains unchanged.
 * For subsequent children, if the previous child has an end date, the current child's
 * start date is set to that end date + 1 day.
 *
 * For time-based durations, the end time is preserved as the start time of the next child.
 * For day-based durations, only the date is used and incremented by 1 day.
 */
function normalizeSequentialChildrenStartDates(children: ScheduleFormWidgetValue[]): ScheduleFormWidgetValue[] {
    if (children.length === 0) {
        return children;
    }

    const result: ScheduleFormWidgetValue[] = [children[0]];

    for (let i = 1; i < children.length; i++) {
        const previousChild = result[i - 1];
        const currentChild = children[i];

        if (!previousChild.endDate) {
            result.push(currentChild);
            continue;
        }

        const previousEndDate = parse(previousChild.endDate, API_DATE_FORMAT, new Date());
        if (!isValid(previousEndDate)) {
            throw new InvalidDateTimeRangeError(
                `Invalid end date in sequential child at index ${i - 1}: ${previousChild.endDate}`
            );
        }

        const isCurrentTimeBased = checkIsTimeBasedDurationUnit(currentChild.duration.unit);
        const isPreviousTimeBased = checkIsTimeBasedDurationUnit(previousChild.duration.unit);

        if (isCurrentTimeBased) {
            if (isPreviousTimeBased) {
                if (!previousChild.endTime) {
                    throw new EndDateRequiredError(
                        `Missing end time in sequential child at index ${i - 1} for time-based duration`
                    );
                }

                const previousEndTime = parse(previousChild.endTime, API_TIME_FORMAT, new Date());
                const previousEndDateTime = mergeDateAndTime(previousEndDate, previousEndTime);

                if (!isValid(previousEndDateTime)) {
                    throw new InvalidDateTimeRangeError(
                        `Invalid end date/time in sequential child at index ${i - 1}: ${previousChild.endDate} ${previousChild.endTime}`
                    );
                }

                const formattedStartDate = format(previousEndDateTime, API_DATE_FORMAT);
                const formattedStartTime = format(previousEndDateTime, API_TIME_FORMAT);

                result.push({
                    ...currentChild,
                    startDate: formattedStartDate,
                    startTime: formattedStartTime
                });
            } else {
                const nextStartDate = DateHelper.add(previousEndDate, 1, TIME_CONSTANTS.d);
                const formattedStartDate = format(nextStartDate, API_DATE_FORMAT);

                result.push({
                    ...currentChild,
                    startDate: formattedStartDate,
                    startTime: DEFAULT_SCHEDULE_TIME_VALUE
                });
            }
        } else {
            const nextStartDate = DateHelper.add(previousEndDate, 1, TIME_CONSTANTS.d);
            const formattedStartDate = format(nextStartDate, API_DATE_FORMAT);

            result.push({
                ...currentChild,
                startDate: formattedStartDate,
                startTime: ''
            });
        }
    }

    return result;
}

/**
 * Calculates parent schedule bounds from children schedules.
 *
 * All children must be in the same mode (either all durationSpecified=true or all false).
 *
 * Parent schedule is always calculated based on:
 * - Parent start = min(child start dates)
 * - Parent end = max(child end dates)
 * - Parent duration = calculated from start to end using Bryntum scheduler
 *
 * @throws MixedDurationSpecifiedError if children are in different modes
 * @throws InvalidStartDateTimeError if any child has invalid start date
 * @throws EndDateRequiredError if any child is missing end date
 * @throws InvalidDateTimeRangeError if any child has invalid end date
 */
async function calculateParentBoundsFromChildren(
    children: Array<ScheduleFormWidgetValue>,
    parentValue: ScheduleFormWidgetValue,
    locale: string = DEFAULT_LANG
): Promise<ScheduleFormWidgetValue> {
    if (children.length === 0) {
        return parentValue;
    }

    // Validate that all children are in the same mode
    const childrenInDurationMode = children[0].durationSpecified;
    for (let i = 1; i < children.length; i++) {
        if (children[i].durationSpecified !== childrenInDurationMode) {
            throw new MixedDurationSpecifiedError();
        }
    }

    // Validate and parse all start and end dates
    const startDates = validateAndParseChildScheduleDates(children, 'startDate', 'startTime');
    const endDates = validateAndParseChildScheduleDates(children, 'endDate', 'endTime');

    const minStart = min(startDates);
    const maxEnd = max(endDates);

    // Build calculation input using min start and max end
    const isTimeBased = checkIsTimeBasedDurationUnit(parentValue.duration.unit);
    const startDate = format(minStart, API_DATE_FORMAT);
    const startTime = isTimeBased ? format(minStart, API_TIME_FORMAT) : '';
    const endDate = format(maxEnd, API_DATE_FORMAT);
    const endTime = isTimeBased ? format(maxEnd, API_TIME_FORMAT) : '';

    const calculationInput: ScheduleCalculationInput = {
        startDate,
        startTime,
        endDate,
        endTime,
        durationUnit: parentValue.duration.unit,
        durationSpecified: false as const,
        calendar: parentValue.calendar,
        locale
    };

    // Use Bryntum to calculate the parent schedule
    const result = await calculateSchedule(calculationInput);

    return makeScheduleValueFromCalculationResult(parentValue, result);
}

/**
 * Validates and parses child schedule dates/times for parent bounds calculation.
 *
 * @param children - Array of child schedule values
 * @param dateField - Which date field to extract ('startDate' or 'endDate')
 * @param timeField - Which time field to extract ('startTime' or 'endTime')
 * @returns Array of parsed Date objects
 */
function validateAndParseChildScheduleDates(
    children: Array<ScheduleFormWidgetValue>,
    dateField: 'startDate' | 'endDate',
    timeField: 'startTime' | 'endTime'
): Date[] {
    const dates: Date[] = [];

    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const dateValue = child[dateField];

        if (!dateValue) {
            throw new InvalidChildDateTimeError(`Missing ${dateField}`, i);
        }

        const parsedDate = parse(dateValue, API_DATE_FORMAT, new Date());
        if (!isValid(parsedDate)) {
            throw new InvalidChildDateTimeError(`Invalid ${dateField}: ${dateValue}`, i);
        }

        const isTimeBased = checkIsTimeBasedDurationUnit(child.duration.unit);

        if (isTimeBased) {
            const timeValue = child[timeField];
            if (!timeValue) {
                throw new InvalidChildDateTimeError(`Missing ${timeField} for time-based duration`, i);
            }

            const parsedTime = parse(timeValue, API_TIME_FORMAT, new Date());
            const parsedDateTime = mergeDateAndTime(parsedDate, parsedTime);

            if (!isValid(parsedDateTime)) {
                throw new InvalidChildDateTimeError(`Invalid ${dateField}/${timeField}: ${dateValue} ${timeValue}`, i);
            }

            dates.push(parsedDateTime);
        } else {
            dates.push(parsedDate);
        }
    }

    return dates;
}
